import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Check, X } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../../../services/api";
import { DsButton, DsIcon } from "../../../design-system";
import { getScreenCatalogForRole, normalizeAccess } from "../../../rbac/catalog";
import { filterCatalogByCompanyModules } from "../../../utils/companyModules";
import ConfirmDialog from "./ConfirmDialog";
import RoleInfoStep from "./RoleInfoStep";
import ModulesStep from "./ModulesStep";
import ReviewStep from "./ReviewStep";
import {
  LEVELS_WITH_ITEMS,
  allowedDataLevels,
  apiErrorMessage,
  applyScreenPreset,
  assignableEmployeeIds,
  isDefaultSystemRole,
  isModuleGranted,
  isSystemRole,
  moduleStats,
  normalizeData,
  normalizeRoleWizardType,
  parseMaybeJson,
  roleUsesAccess,
  sameDataAccess,
  sameScreenAccess,
  sanitizeScreenAccessForSubmit,
  setModuleEnabled,
  SWITCHABLE_MODULE_KEYS,
} from "./roleAccessUtils";

const STEPS = [
  { id: "info", label: "Role info" },
  { id: "modules", label: "Modules" },
  { id: "review", label: "Review" },
];

/**
 * Create / copy role wizard: Role info → Modules → Review.
 * Screen permissions and data scope are refined on the Permissions tab (one place).
 * Assignment (all / selected employees) stays on Role info for bulk assign.
 */
export default function RoleWizard({
  target,
  roles,
  accessByRole,
  dataByRole,
  branches,
  departments,
  filterResults,
  setFilterResults,
  setShowFilters,
  setFormData,
  activeEmployeeCount,
  isAdmin,
  delegate,
  canEditScreenAccess,
  canEditDataAccess,
  onClose,
  onSaved,
  onRoleCreated,
}) {
  const role = target.role || null;
  const isSystem = isSystemRole(role);
  // Shown catalog follows the superadmin module flags; stored access for hidden modules is left untouched.
  const fullCatalog = useMemo(() => getScreenCatalogForRole(role), [role]);
  const catalog = useMemo(() => filterCatalogByCompanyModules(fullCatalog), [fullCatalog]);
  const originalType = role ? (isSystem ? "access_based" : normalizeRoleWizardType(role)) : null;
  const copySource = target.copyFromRoleId != null ? roles.find((r) => String(r.id) === String(target.copyFromRoleId)) : null;

  const [initial] = useState(() => {
    const sourceId = role?.id ?? copySource?.id;
    const access = sourceId != null && accessByRole[sourceId] ? accessByRole[sourceId] : normalizeAccess({}, fullCatalog);
    // System roles without a saved level are resolved as "all" by the backend (normalizeUiDataAccess).
    const storedData = (id) => {
      const d = normalizeData(dataByRole[id]);
      return isSystem && !d.level ? { level: "all", selectedItems: [] } : d;
    };
    return {
      draft: {
        name: role?.name || "",
        description: role?.description || "",
        roleType: role ? originalType : copySource ? "access_based" : null,
        active: role ? role.active !== false : true,
        assignmentType: ["all", "filter"].includes(role?.assignmentType) ? role.assignmentType : null,
        delegableModules: Array.isArray(role?.delegableModules) ? role.delegableModules : [],
      },
      access,
      // Default company-wide scope; refine later on Permissions tab.
      data: sourceId != null ? storedData(sourceId) : { level: "all", selectedItems: [] },
      filterIds: assignableEmployeeIds(parseMaybeJson(role?.filterData)).join(","),
      // Baseline for "did this save change anything" — a copy starts from an empty role.
      savedAccess: role ? sanitizeScreenAccessForSubmit(access) : {},
      savedData: role ? storedData(role.id) : { level: "all", selectedItems: [] },
    };
  });

  const [draft, setDraft] = useState(initial.draft);
  const [access, setAccess] = useState(initial.access);
  const [dataAccess, setDataAccess] = useState(initial.data);
  const [selectedModules, setSelectedModules] = useState(
    () => new Set(catalog.filter((m) => isModuleGranted(m, initial.access[m.key])).map((m) => m.key))
  );
  const [stepId, setStepId] = useState(isSystem ? "modules" : "info");
  const [attempted, setAttempted] = useState(() => new Set());
  const [confirm, setConfirm] = useState(null);
  const [saving, setSaving] = useState(false);
  const savedRoleIdRef = useRef(null);
  const baselineRef = useRef({ assignmentType: initial.draft.assignmentType, filterIds: initial.filterIds });
  const bodyRef = useRef(null);
  const dialogRef = useRef(null);

  // The shared Filters modal (owned by GlobalSettingsModal) writes into filterResults.
  useEffect(() => {
    setFilterResults(parseMaybeJson(role?.filterData));
    return () => setFilterResults(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  }, [stepId]);

  useEffect(() => {
    if (!dialogRef.current?.contains(document.activeElement)) dialogRef.current?.focus();
  }, []);

  const usesAccess = isSystem || draft.roleType === "access_based";
  // Copy: skip modules if access already prefilled; action-based: info → review only.
  const stepIds =
    draft.roleType === "action_based" && !isSystem
      ? ["info", "review"]
      : copySource
        ? ["info", "modules", "review"]
        : STEPS.map((s) => s.id);
  const stepIndex = stepIds.indexOf(stepId);
  const readOnlyScreens = !canEditScreenAccess;
  const readOnlyData = !canEditDataAccess;
  const accessTypeBlocked = !canEditScreenAccess && !canEditDataAccess;
  const filterCount = draft.assignmentType === "filter" ? assignableEmployeeIds(filterResults).length : 0;
  const lockedModules = useMemo(
    () => new Set(delegate ? catalog.map((m) => m.key).filter((k) => !delegate.modules.has(k)) : []),
    [catalog, delegate]
  );
  const allowedLevels = useMemo(() => allowedDataLevels(delegate ? delegate.ownScope : null), [delegate]);
  const patchDraft = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const currentRoleId = role?.id ?? savedRoleIdRef.current;

  const validate = (id) => {
    if (id === "info") {
      if (isSystem) return null;
      const name = draft.name.trim();
      if (!name) return "Enter a role name.";
      const dupe = roles.some(
        (r) => String(r.id) !== String(currentRoleId) && String(r.name || "").trim().toLowerCase() === name.toLowerCase()
      );
      if (dupe) return "Another role already uses this name. Choose a different name.";
      if (!draft.roleType) return "Choose a role type: Access Based or Action Based.";
      if (draft.roleType === "access_based" && accessTypeBlocked && originalType !== "access_based") {
        return "You don't have permission to set screen or data access. Choose Action Based.";
      }
      if (!draft.assignmentType) return "Choose who gets this role: All employees or Selected employees.";
      if (draft.assignmentType === "filter" && filterCount === 0) {
        return "Select at least one employee, or choose All employees.";
      }
      return null;
    }
    if (id === "modules") {
      if (readOnlyScreens) return null;
      return selectedModules.size === 0 ? "Select at least one module." : null;
    }
    return null;
  };

  const markAttempted = (id) => setAttempted((prev) => new Set(prev).add(id));

  const goTo = (id) => {
    const targetIdx = stepIds.indexOf(id);
    if (targetIdx < 0) return;
    for (let i = 0; i < targetIdx; i += 1) {
      if (i < stepIndex) continue;
      if (validate(stepIds[i])) {
        markAttempted(stepIds[i]);
        setStepId(stepIds[i]);
        return;
      }
    }
    setStepId(id);
  };
  const goNext = () => goTo(stepIds[stepIndex + 1]);
  const goBack = () => stepIndex > 0 && setStepId(stepIds[stepIndex - 1]);

  // --- Module selection ----------------------------------------------------
  const isSwitchable = (key) => isDefaultSystemRole(role) && SWITCHABLE_MODULE_KEYS.has(key);
  const clearModule = (prevAccess, mod) => ({
    ...prevAccess,
    [mod.key]: isSwitchable(mod.key)
      ? setModuleEnabled(prevAccess[mod.key], false)
      : applyScreenPreset(prevAccess[mod.key], mod, mod.screens.map((s) => s.key), "none"),
  });
  const enableSwitchable = (prevAccess, keys) =>
    keys.filter(isSwitchable).reduce((acc, k) => ({ ...acc, [k]: setModuleEnabled(acc[k], true) }), prevAccess);
  /** Selecting a module seeds View on its screens; refine actions on the Permissions tab. */
  const seedModuleView = (prevAccess, mod) =>
    applyScreenPreset(prevAccess[mod.key], mod, mod.screens.map((s) => s.key), "view");

  const toggleModule = (key) => {
    const mod = catalog.find((m) => m.key === key);
    if (!mod || lockedModules.has(key)) return;
    if (selectedModules.has(key)) {
      setAccess((prev) => clearModule(prev, mod));
      setSelectedModules((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } else {
      setAccess((prev) => {
        let next = enableSwitchable(prev, [key]);
        next = { ...next, [mod.key]: seedModuleView(next, mod) };
        return next;
      });
      setSelectedModules((prev) => new Set(prev).add(key));
    }
  };
  const selectAllModules = () => {
    const keys = catalog.map((m) => m.key).filter((k) => !lockedModules.has(k));
    setAccess((prev) => {
      let next = enableSwitchable(prev, keys);
      for (const key of keys) {
        const mod = catalog.find((m) => m.key === key);
        if (mod) next = { ...next, [mod.key]: seedModuleView(next, mod) };
      }
      return next;
    });
    setSelectedModules((prev) => new Set([...prev, ...keys]));
  };
  const clearAllModules = () => {
    setAccess((prev) => catalog.filter((m) => !lockedModules.has(m.key)).reduce(clearModule, prev));
    setSelectedModules((prev) => new Set([...prev].filter((k) => lockedModules.has(k))));
  };
  const copySources = roles.filter(
    (r) => String(r.id) !== String(currentRoleId) && roleUsesAccess(r) && isSystemRole(r) === isSystem && accessByRole[r.id]
  );
  const applyCopy = (sourceId) => {
    const source = accessByRole[sourceId];
    if (!source) return;
    const next = { ...access };
    for (const mod of catalog) if (!lockedModules.has(mod.key)) next[mod.key] = source[mod.key];
    setAccess(next);
    setSelectedModules(
      new Set(catalog.filter((m) => (lockedModules.has(m.key) ? selectedModules.has(m.key) : isModuleGranted(m, next[m.key]))).map((m) => m.key))
    );
    const name = roles.find((r) => String(r.id) === String(sourceId))?.name || "the role";
    toast.success(`Copied permissions from ${name}.`);
  };
  const requestCopy = (sourceId) => {
    const hasAny = catalog.some((m) => !lockedModules.has(m.key) && isModuleGranted(m, access[m.key]));
    if (hasAny) setConfirm({ kind: "copy", sourceId });
    else applyCopy(sourceId);
  };

  // --- Assignment ----------------------------------------------------------
  const openFilters = () => {
    setFormData((prev) => ({ ...prev, filterData: filterResults || null }));
    setShowFilters(true);
  };
  const chooseAll = () => {
    if (draft.assignmentType !== "all") setConfirm({ kind: "all" });
  };
  const chooseFilter = () => {
    patchDraft({ assignmentType: "filter" });
    if (assignableEmployeeIds(filterResults).length === 0) openFilters();
  };

  // --- Dirty / close -------------------------------------------------------
  const isDirty =
    JSON.stringify(draft) !== JSON.stringify(initial.draft) ||
    !sameScreenAccess(sanitizeScreenAccessForSubmit(access), sanitizeScreenAccessForSubmit(initial.access)) ||
    !sameDataAccess(dataAccess, initial.data) ||
    assignableEmployeeIds(filterResults).join(",") !== initial.filterIds;
  const requestClose = () => {
    if (saving) return;
    if (isDirty) setConfirm({ kind: "discard" });
    else onClose();
  };

  // --- Save ----------------------------------------------------------------
  const userRolesGranted = selectedModules.has("user_roles") && isModuleGranted(catalog.find((m) => m.key === "user_roles"), access.user_roles);
  const switchingToAction = !!role && originalType === "access_based" && draft.roleType === "action_based";

  const save = async ({ typeSwitchConfirmed = false } = {}) => {
    if (saving) return;
    for (const id of stepIds) {
      if (validate(id)) {
        markAttempted(id);
        setStepId(id);
        return;
      }
    }
    if (switchingToAction && !typeSwitchConfirmed) {
      setConfirm({ kind: "typeSwitch" });
      return;
    }

    setSaving(true);
    let roleId = currentRoleId;
    const isNew = roleId == null;
    try {
      if (!isSystem) {
        const filterIds = assignableEmployeeIds(filterResults).join(",");
        const assignmentChanged =
          draft.assignmentType !== baselineRef.current.assignmentType ||
          (draft.assignmentType === "filter" && filterIds !== baselineRef.current.filterIds);
        const payload = {
          name: draft.name.trim(),
          description: draft.description.trim(),
          active: draft.active,
          roleType: draft.roleType,
        };
        if (isAdmin) payload.delegableModules = usesAccess && userRolesGranted ? draft.delegableModules : [];
        if (isNew || assignmentChanged) {
          payload.assignmentType = draft.assignmentType;
          payload.filterData = draft.assignmentType === "filter" ? filterResults : null;
        }
        if (isNew) {
          const res = await api.post("/global-settings/roles", { ...payload, type: "custom", level: "group" });
          roleId = res?.data?.id ?? res?.data?.data?.id;
          if (roleId == null) throw new Error("The role was created but no id was returned.");
          savedRoleIdRef.current = roleId;
          onRoleCreated();
        } else {
          await api.put(`/global-settings/roles/${roleId}`, payload);
        }
        baselineRef.current = { assignmentType: draft.assignmentType, filterIds };
      }

      if (usesAccess) {
        const cleaned = sanitizeScreenAccessForSubmit(access);
        if (!readOnlyScreens && (isNew || !sameScreenAccess(cleaned, initial.savedAccess))) {
          await api.post("/global-settings/screen-access", { roleId, screenAccess: cleaned });
        }
        const nextData = {
          level: dataAccess.level,
          selectedItems: LEVELS_WITH_ITEMS.has(dataAccess.level) ? dataAccess.selectedItems : [],
        };
        if (!readOnlyData && (isNew || !sameDataAccess(nextData, initial.savedData))) {
          await api.post("/global-settings/data-access", { roleId, dataAccess: nextData });
        }
      }

      toast.success(role ? "Role updated." : "Role created.");
      onSaved(roleId);
    } catch (err) {
      const msg = apiErrorMessage(err, "Could not save the role. Please try again.");
      toast.error(savedRoleIdRef.current && !role ? `Role created, but saving its access failed: ${msg} Fix and save again.` : msg);
    } finally {
      setSaving(false);
    }
  };

  const warnings = [];
  if (switchingToAction) warnings.push("Saving as Action Based removes this role's screen and data access.");
  if (!isSystem && draft.assignmentType === "all" && baselineRef.current.assignmentType !== "all") {
    warnings.push("All active employees will get this role, replacing any other custom role they have.");
  }
  if (!draft.active && !isSystem) warnings.push("The role is saved as Inactive: assigned employees get only default employee access.");
  if (readOnlyScreens && usesAccess) warnings.push("You can view but not change screen permissions; they will be saved unchanged.");
  if (readOnlyData && usesAccess) warnings.push("You can view but not change data access; it will be saved unchanged.");

  const stepError = attempted.has(stepId) ? validate(stepId) : null;
  const title = role ? `Edit role: ${role.name}` : copySource ? `New role from ${copySource.name}` : "Create role";

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/50 p-2 sm:p-6" role="presentation">
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="urm-wizard-title"
        onKeyDown={(e) => {
          if (e.key === "Escape" && !confirm) {
            e.stopPropagation();
            requestClose();
          }
        }}
        className="flex h-[min(94vh,900px)] w-full max-w-6xl flex-col overflow-hidden rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-2xl outline-none"
      >
        <header className="shrink-0 border-b border-[hsl(var(--ds-border))]">
          <div className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="min-w-0">
              <h2 id="urm-wizard-title" className="ds-h3 truncate text-[hsl(var(--ds-fg))]">
                {title}
              </h2>
              <p className="text-xs text-[hsl(var(--ds-muted-fg))]">
                Step {stepIndex + 1} of {stepIds.length} · {STEPS.find((s) => s.id === stepId)?.label}
              </p>
            </div>
            <button
              type="button"
              onClick={requestClose}
              aria-label="Close"
              className="rounded-md p-1.5 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))] hover:text-[hsl(var(--ds-fg))] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))]"
            >
              <DsIcon icon={X} size="md" />
            </button>
          </div>
          <ol className="flex gap-1 overflow-x-auto px-5 pb-3" aria-label="Wizard steps">
            {STEPS.map((s, i) => {
              const enabled = stepIds.includes(s.id);
              const idx = stepIds.indexOf(s.id);
              const done = enabled && idx < stepIndex;
              const current = s.id === stepId;
              return (
                <li key={s.id} className="flex min-w-[120px] flex-1 items-center">
                  <button
                    type="button"
                    disabled={!enabled || saving}
                    aria-current={current ? "step" : undefined}
                    title={!enabled ? "Not needed for Action Based roles" : undefined}
                    onClick={() => goTo(s.id)}
                    className="flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] disabled:cursor-not-allowed"
                  >
                    <span
                      className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-semibold ${
                        current
                          ? "bg-[hsl(var(--ds-primary))] text-[hsl(var(--ds-primary-fg))]"
                          : done
                            ? "bg-[hsl(var(--ds-primary)/0.15)] text-[hsl(var(--ds-primary))]"
                            : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                    </span>
                    <span
                      className={`truncate text-xs font-medium ${
                        !enabled
                          ? "text-[hsl(var(--ds-muted-fg))] line-through"
                          : current
                            ? "text-[hsl(var(--ds-fg))]"
                            : "text-[hsl(var(--ds-muted-fg))]"
                      }`}
                    >
                      {s.label}
                    </span>
                  </button>
                  {i < STEPS.length - 1 ? <span className="mx-1 hidden h-px w-4 shrink-0 bg-[hsl(var(--ds-border))] sm:block" /> : null}
                </li>
              );
            })}
          </ol>
        </header>

        <div ref={bodyRef} className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[hsl(var(--ds-bg))] px-5 py-5">
          {stepId === "info" ? (
            <RoleInfoStep
              draft={draft}
              patchDraft={patchDraft}
              isSystem={isSystem}
              isEdit={!!role}
              originalType={originalType}
              error={stepError}
              filterCount={filterCount}
              activeEmployeeCount={activeEmployeeCount}
              accessTypeBlocked={accessTypeBlocked && originalType !== "access_based"}
              isDelegate={!!delegate}
              onChooseAll={chooseAll}
              onChooseFilter={chooseFilter}
              onOpenFilters={openFilters}
            />
          ) : null}
          {stepId === "modules" ? (
            <ModulesStep
              catalog={catalog}
              access={access}
              selectedModules={selectedModules}
              lockedModules={lockedModules}
              readOnly={readOnlyScreens}
              error={stepError}
              copySources={copySources}
              onToggleModule={toggleModule}
              onSelectAll={selectAllModules}
              onClearAll={clearAllModules}
              onCopyFrom={requestCopy}
            />
          ) : null}
          {stepId === "review" ? (
            <ReviewStep
              draft={draft}
              isSystem={isSystem}
              usesAccess={usesAccess}
              catalog={catalog}
              access={access}
              selectedModules={selectedModules}
              lockedModules={lockedModules}
              dataAccess={dataAccess}
              branches={branches}
              departments={departments}
              filterCount={filterCount}
              activeEmployeeCount={activeEmployeeCount}
              isAdmin={isAdmin}
              delegableModules={draft.delegableModules}
              warnings={[
                ...warnings,
                ...(usesAccess
                  ? [
                      "After save, open the Permissions tab to set screen actions and data scope for this role.",
                    ]
                  : []),
              ]}
              onEditStep={goTo}
            />
          ) : null}
        </div>

        <footer className="flex shrink-0 items-center justify-between gap-2 border-t border-[hsl(var(--ds-border))] px-5 py-3">
          <DsButton variant="ghost" size="sm" onClick={requestClose} disabled={saving}>
            Cancel
          </DsButton>
          <div className="flex items-center gap-2">
            {stepIndex > 0 ? (
              <DsButton variant="outline" size="sm" onClick={goBack} disabled={saving}>
                Back
              </DsButton>
            ) : null}
            {stepId === "review" ? (
              <DsButton size="md" onClick={() => save()} loading={saving}>
                {saving ? "Saving…" : "Save role"}
              </DsButton>
            ) : (
              <DsButton size="md" onClick={goNext}>
                Next: {STEPS.find((s) => s.id === stepIds[stepIndex + 1])?.label}
              </DsButton>
            )}
          </div>
        </footer>
      </div>

      <ConfirmDialog
        open={confirm?.kind === "all"}
        tone="warning"
        title="Assign to all employees?"
        confirmLabel="Assign to all"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          patchDraft({ assignmentType: "all" });
          setConfirm(null);
        }}
      >
        {Number.isFinite(activeEmployeeCount)
          ? `All ${activeEmployeeCount} active employees will get this role when you save.`
          : "Every active employee will get this role when you save."}{" "}
        An employee holds one custom role at a time, so anyone on another custom role moves to this one.
        {delegate ? " You are not affected." : ""}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "copy"}
        tone="warning"
        title="Replace current permissions?"
        confirmLabel="Replace"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          applyCopy(confirm.sourceId);
          setConfirm(null);
        }}
      >
        The modules and screen permissions you picked will be replaced by the selected role&apos;s.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "discard"}
        title="Discard changes?"
        confirmLabel="Discard"
        cancelLabel="Keep editing"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          onClose();
        }}
      >
        Nothing has been saved yet. Your changes to this role will be lost.
      </ConfirmDialog>
      <ConfirmDialog
        open={confirm?.kind === "typeSwitch"}
        title="Switch to Action Based?"
        confirmLabel="Switch and save"
        onCancel={() => setConfirm(null)}
        onConfirm={() => {
          setConfirm(null);
          save({ typeSwitchConfirmed: true });
        }}
      >
        This removes the role&apos;s screen and data access. Employees with this role lose that access.
      </ConfirmDialog>
    </div>,
    document.body
  );
}
